import { useState, useMemo, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export interface UseTableSortOptions<T> {
  /** Initial sort configuration */
  initialSort?: SortConfig;
  /** Custom sort comparators for specific fields */
  comparators?: Record<string, (a: T, b: T) => number>;
}

export interface UseTableSortReturn<T> {
  /** Current sort configuration */
  sortConfig: SortConfig;
  /** Sorted data */
  sortedData: T[];
  /** Request a sort on a specific column */
  requestSort: (key: string) => void;
  /** Get sort direction for a column (for displaying indicators) */
  getSortDirection: (key: string) => SortDirection;
}

/**
 * Hook for managing table sorting state and logic.
 *
 * @example
 * const { sortedData, requestSort, getSortDirection } = useTableSort(items, {
 *   initialSort: { key: 'name', direction: 'asc' },
 *   comparators: {
 *     date: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
 *   },
 * });
 */
export function useTableSort<T>(
  data: T[],
  options: UseTableSortOptions<T> = {}
): UseTableSortReturn<T> {
  const { initialSort = { key: '', direction: null }, comparators = {} } = options;
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSort);

  const requestSort = useCallback((key: string) => {
    setSortConfig((prevConfig) => {
      // Cycle through: asc -> desc -> null (no sort)
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: '', direction: null };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const getSortDirection = useCallback(
    (key: string): SortDirection => {
      if (sortConfig.key === key) {
        return sortConfig.direction;
      }
      return null;
    },
    [sortConfig]
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return data;
    }

    return [...data].sort((a, b) => {
      // Use custom comparator if available
      if (comparators[sortConfig.key]) {
        const result = comparators[sortConfig.key](a, b);
        return sortConfig.direction === 'asc' ? result : -result;
      }

      // Default comparison - access properties via indexer
      const aObj = a as Record<string, unknown>;
      const bObj = b as Record<string, unknown>;
      const aVal = aObj[sortConfig.key];
      const bVal = bObj[sortConfig.key];

      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle strings (case-insensitive)
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      const comparison = aStr.localeCompare(bStr);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig, comparators]);

  return {
    sortConfig,
    sortedData,
    requestSort,
    getSortDirection,
  };
}
